import { Component } from '@angular/core';
import { NgClass } from '@angular/common';
import { CrudService } from 'src/app/services/crud.service';
import CustomStore from 'devextreme/data/custom_store';
import { environment } from 'src/environments/environment';
import { NotifyService } from '../services/notify.service';
import { HorizontalMenuService } from '../services/horizontalmenu.service';
import { DateFormatService } from '../services/date-format.service';
import { DxBoxModule, DxButtonModule, DxDataGridModule, DxLoadPanelModule, DxLookupModule, DxValidatorModule } from 'devextreme-angular';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { PageHeaderComponent } from 'src/app/shared/page-header/page-header.component';

/**
 * Raw feeds are host_promoflo data sources, as the API names them (`feed` / `data_source`
 * query parameter and `meta.feeds`): ALM, PAR or LMX (tech-spec-ilr-alm-host-feed-to-host-promoflo).
 */
export type RawFeed = string;

/** Display labels for the Feed lookup; an unknown data source shows its code. */
export const RAW_FEED_LABELS: Record<string, string> = {
  ALM: 'ALM',
  PAR: 'Paramount',
  LMX: 'LiquidMix',
};

@Component({
    selector: 'app-raw-host-data',
    templateUrl: './raw-host-data.component.html',
    styleUrls: ['./raw-host-data.component.css'],
    imports: [ NgClass, DxBoxModule, DxLookupModule, DxValidatorModule, DxButtonModule, DxDataGridModule, DxLoadPanelModule, PageHeaderComponent],
    standalone: true
})
export class RawHostDataComponent {
  dataSource: CustomStore;
  fileDates: string[] = [];
  accountCodes: string[] = [];
  dataLoading: boolean = false;
  fileDate: string = '';
  accountCode: string = '';
  viewHostData: boolean = false;
  rollBackDisabled: boolean = true;
  /** Feeds this tenant may view, as returned by host-data-raw/{token} meta. Single entry = no Feed lookup. */
  feeds: RawFeed[] = [];
  feed: RawFeed = '';
  feedOptions: { value: RawFeed; label: string }[] = [];
  /** Toggled off/on around a feed change so the grid is re-created under the new storageKey. */
  gridReady: boolean = true;
  private locationToken: string = '';
  private apiUrl: string = environment.baseApiUrl;

  constructor(private crudService: CrudService,
              private notify: NotifyService,
              private horizontalMenuService: HorizontalMenuService,
              private dateFormatService: DateFormatService)
  {

    const userDetails = localStorage.getItem('userDetails');
    const parsedDetails = JSON.parse(userDetails || '{}');
    this.locationToken = parsedDetails?.data?.location_token ?? '';
    // No feed parameter = the tenant's default feed; the response meta tells us which
    // feeds are viewable so the Feed lookup only appears for two-feed tenants.
    this.crudService.getData(`${this.apiUrl}host-data-raw/${this.locationToken}`).subscribe((result: any) => {
      this.fileDates = result.data;
      this.applyFeedMeta(result.meta);
      this.loadAccountCodes();
    });
    this.horizontalMenuService.viewHostData$.subscribe((value: boolean) => {
      this.viewHostData = value;
    });
  }

  get feedLabel(): string {
    return RAW_FEED_LABELS[this.feed] ?? this.feed;
  }

  /** Grid state is kept per feed so one feed's column layout is never applied to another's fields. */
  get gridStorageKey(): string {
    return `rawHostDataState:${this.feed}`;
  }

  /** Paramount has no account dimension for per_buying_group tenants (Thirsty Camel). */
  get accountLookupDisabled(): boolean {
    return this.accountCodes.length === 0;
  }

  private applyFeedMeta(meta: any): void
  {
    const feeds: RawFeed[] = Array.isArray(meta?.feeds) && meta.feeds.length > 0 ? meta.feeds : (meta?.feed ? [meta.feed] : []);
    this.feeds = feeds;
    this.feed = feeds.includes(meta?.feed) ? meta.feed : (feeds[0] ?? '');
    this.feedOptions = feeds.map((value) => ({ value, label: RAW_FEED_LABELS[value] ?? value }));
  }

  private loadAccountCodes(): void
  {
    this.crudService.getData(`${this.apiUrl}host-data/${this.locationToken}?feed=${this.feed}`).subscribe((result: any) => {
      this.accountCodes = result.data ?? [];
    });
  }

  private loadFileDates(): void
  {
    this.crudService.getData(`${this.apiUrl}host-data-raw/${this.locationToken}?feed=${this.feed}`).subscribe((result: any) => {
      this.fileDates = result.data ?? [];
    });
  }

  setFeed(e: any)
  {
    const next: RawFeed = e.value;
    if (!next || next === this.feed) {
      return;
    }
    this.feed = next;
    // A feed change invalidates everything chosen for the previous feed.
    this.fileDate = '';
    this.accountCode = '';
    this.fileDates = [];
    this.accountCodes = [];
    this.dataSource = undefined as any;
    this.rollBackDisabled = true;
    // Re-create the grid so DevExtreme picks up the per-feed storageKey.
    this.gridReady = false;
    setTimeout(() => { this.gridReady = true; });
    this.loadFileDates();
    this.loadAccountCodes();
  }

  setDate(e: any)
  {
    this.fileDate = e.value;
  }

  setAccountCode(e: any)
  {
    this.accountCode = e.value;
  }

  loadData()
  {
    // An account is required unless the selected feed has no account split.
    const accountRequired = !this.accountLookupDisabled;
    if(this.fileDate === '' || (accountRequired && this.accountCode === '')) {
      this.notify.returnError(accountRequired ? 'Please select a file date and account code' : 'Please select a file date');
      return;
    }
    this.dataSource = new CustomStore({
      key: 'liquorfile_number',
      load: async () => {
        this.dataLoading = true;
        return await new Promise((resolve) => {
          this.crudService.getData(`${this.apiUrl}host-data-raw?feed=${this.feed}&date_added=${this.fileDate}&account_code=${this.accountCode}`).subscribe((result: any) => {
            resolve(result);
            this.rollBackDisabled = false;
            this.dataLoading = false;
          }, (error: any) => {
            console.log(error);
            this.dataLoading = false;
          });
        })
      },
      insert: async (values: any) => {
        //
      },
      update: async (key, values: any) => {
        //
      },
      remove: async (key) => {
        //
      }
    });
  }

  rollBackData()
  {
    // Rollback is unchanged by the Feed lookup: it reruns the full sync for the date across
    // every feed the tenant loads (config/host_feeds.php), whichever feed is being viewed.
    if(this.fileDate === '' || this.accountCode === '') {
      this.notify.returnError('Please select a file date and account code');
      return;
    }
    this.rollBackDisabled = true;
    this.dataLoading = true;
    this.crudService.getData(`${this.apiUrl}host-data?date_added=${this.dateFormatService.yearFirstDateFormat(this.fileDate)}&account_code=${this.accountCode}&rollback`).subscribe((result: any) => {
      this.rollBackDisabled = false;
      this.dataLoading = false;
      this.notify.tellUser(result);
    });
  }
  sanitizeSheetName(name: string): string {
      return name.replace(/[:\\/?*\[\]]/g, '_');
  }

  /** "<feed label> <account> <date>" — the feed label only when the tenant can switch feeds. */
  exportName(): string {
    const parts = [this.accountCode, this.fileDate].filter((p) => p !== '');
    if (this.feeds.length > 1) {
      parts.unshift(this.feedLabel);
    }
    return this.sanitizeSheetName(parts.join(' '));
  }

  onExporting(e: any) {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(this.exportName());

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if(gridCell?.rowType === 'header') {
              excelCell.font = { bold: true };
              excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '6ABDC8' } };
            }
          }
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `Host Data ${this.exportName()}.xlsx`);
          });
        });
      }

}
